import { serverTimestamp } from 'firebase/firestore';
import { Users, Weddings } from '@bridebook/models';
import { Tasks as UsersTasks } from '@bridebook/models/source/models/Users/Tasks';
import { ITask as IUserTask } from '@bridebook/models/source/models/Users/Tasks.types';
import { Tasks as WeddingTasks } from '@bridebook/models/source/models/Weddings/Tasks';
import { ITask as IWeddingTask } from '@bridebook/models/source/models/Weddings/Tasks.types';
import gazetteer from '@bridebook/toolbox/src/gazetteer';
import { SentryMinimal } from '@bridebook/toolbox/src/sentry';
import { taskSlice } from '@bridebook/web/lib/task/slice';
import {
  fastForwardCompletedSessions,
  getNextTaskFlowInstance,
  getPreviousTaskFlowInstance,
  getTaskFlowInstance,
  migrateToNewTaskFlowDefinition,
  saveTaskFlowInstance,
  registeredTodayTasks,
  signalTaskFlow,
  TTaskDefinitionUI,
} from '@bridebook/web/lib/task/task-flow-engine';
import { IDeps } from '@bridebook/web/lib/types';
import { selectCurrentUser } from '@bridebook/web/lib/users/selectors';
import { assertState } from '@bridebook/web/lib/utils/assertState';
import { getWeddingProfileId } from '@bridebook/web/lib/weddings/selectors';
import { newTodaysTaskSessionAnalytics } from '../dashboard/analytics/actions';
import { noopAction } from '../utils';
import { checkIsNewSession } from './check-is-new-session';
import { getTaskFlowDefinitionUI } from './hooks/query/use-today-task-flow-definition';
import { recursiveSerializeTimestamps } from '@bridebook/toolbox/src/serialize-timestamps';

const markTodayTaskInFlight = new Set<string>();

type MarkTodayTaskAsCompletedOptions = {
  onCompleted?: () => void;
  onError?: (error: unknown) => void;
};

const isTaskAlreadyCompletedInState = (
  state: ReturnType<IDeps['getState']>,
  task: Pick<TTaskDefinitionUI, 'id' | 'storage'>,
) => {
  if (task.storage === 'none' || state.task.todayTasks.status === 'notLoaded') return false;
  const tasksByStorage = state.task.todayTasks[task.storage];
  return Boolean(tasksByStorage?.[task.id]?.done);
};

const getMarkTodayTaskInFlightKey = (
  state: ReturnType<IDeps['getState']>,
  task: Pick<TTaskDefinitionUI, 'id' | 'storage'>,
) => {
  if (task.storage === 'wedding') {
    const weddingId = getWeddingProfileId(state);
    return weddingId ? `wedding:${weddingId}:${task.id}` : null;
  }
  if (task.storage === 'user') {
    const currentUser = selectCurrentUser(state);
    return currentUser ? `user:${currentUser.id}:${task.id}` : null;
  }
  return null;
};

export const initializeTaskFlows =
  () =>
  ({ getState, dispatch }: IDeps) => {
    const weddingCountry = getState().weddings.profile.l10n.country;
    const market = gazetteer.getMarketByCountry(weddingCountry);
    const taskFlowApiDefinition = getState().task.todayTaskFlowDefinition;
    assertState(taskFlowApiDefinition, 'loaded');
    const taskFlowDefinition = getTaskFlowDefinitionUI(taskFlowApiDefinition.data);
    if (!taskFlowDefinition) return taskFlowsInitializationFailed();
    try {
      const previousTaskFlowInstance = getPreviousTaskFlowInstance(taskFlowDefinition, {
        getState,
        market,
      });

      const nextTaskFlowInstance = getTaskFlowInstance(taskFlowDefinition, {
        getState,
        market,
      });

      dispatch(taskFlowsInitialized({ todayTaskFlowInstance: nextTaskFlowInstance }));

      if (checkIsNewSession(previousTaskFlowInstance, nextTaskFlowInstance)) {
        dispatch(
          newTodaysTaskSessionAnalytics({ sessionNumber: nextTaskFlowInstance.currentSession.id }),
        );
      }
      return noopAction();
    } catch (e) {
      SentryMinimal().captureException(e, {
        tags: {
          source: 'taskFlows',
        },
      });
      // eslint-disable-next-line no-console
      console.error('Task flows initialization failed', e);
      return taskFlowsInitializationFailed();
    }
  };

export const interactWithTodayTask =
  () =>
  ({ dispatch }: IDeps) => {
    dispatch(signalTodayTaskFlow());
    return noopAction();
  };

export const signalTodayTaskFlow =
  () =>
  ({ getState, dispatch }: IDeps) => {
    const weddingCountry = getState().weddings.profile.l10n.country;
    const market = gazetteer.getMarketByCountry(weddingCountry);

    const taskFlowApiDefinition = getState().task.todayTaskFlowDefinition;
    assertState(taskFlowApiDefinition, 'loaded');
    const taskFlowDefinition = getTaskFlowDefinitionUI(taskFlowApiDefinition.data);
    if (!taskFlowDefinition) return noopAction();

    const previousTaskFlowInstance = getPreviousTaskFlowInstance(taskFlowDefinition, {
      getState,
      market,
    });

    const nextTaskFlowInstance = signalTaskFlow(taskFlowDefinition, {
      getState,
      market,
    });

    dispatch(updateTodayTaskFlow({ todayTaskFlowInstance: nextTaskFlowInstance }));
    if (checkIsNewSession(previousTaskFlowInstance, nextTaskFlowInstance)) {
      dispatch(
        newTodaysTaskSessionAnalytics({ sessionNumber: nextTaskFlowInstance.currentSession.id }),
      );
    }
    return noopAction();
  };

export const markTodayTaskAsCompleted =
  (
    task: Pick<TTaskDefinitionUI, 'id' | 'storage' | 'name'>,
    options?: MarkTodayTaskAsCompletedOptions,
  ) =>
  ({ getState, dispatch }: IDeps) => {
    const asyncAction = async () => {
      const state = getState();
      const inFlightKey = getMarkTodayTaskInFlightKey(state, task);
      if (inFlightKey && markTodayTaskInFlight.has(inFlightKey)) return;
      if (inFlightKey) markTodayTaskInFlight.add(inFlightKey);

      try {
        let wasTaskAlreadyCompleted = false;
        if (task.storage === 'wedding') {
          if (isTaskAlreadyCompletedInState(state, task)) {
            options?.onCompleted?.();
            return;
          }

          const weddingId = getWeddingProfileId(state);
          const weddingRef = Weddings._.getById(weddingId);
          const dbWeddingTaskRef = weddingRef.Tasks.getById(task.id);
          const dbWeddingTaskRaw = await dbWeddingTaskRef.get(true);
          wasTaskAlreadyCompleted = Boolean(dbWeddingTaskRaw?.done);
          const dbWeddingTask = dbWeddingTaskRaw
            ? recursiveSerializeTimestamps(dbWeddingTaskRaw)
            : undefined;

          if (!wasTaskAlreadyCompleted) {
            const saveTask: Omit<IWeddingTask, 'createdAt'> = {
              id: task.id,
              group: WeddingTasks.todayTaskGroupId,
              custom: false,
              period: '+P',
              order: 1,
              name: task.name,
              done: true,
            };
            await dbWeddingTaskRef.set(
              dbWeddingTaskRaw
                ? { ...saveTask, createdAt: dbWeddingTaskRaw.createdAt }
                : { ...saveTask, createdAt: serverTimestamp() },
            );
            const updatedTaskRaw = await dbWeddingTaskRef.get(true);
            const updatedTask = recursiveSerializeTimestamps(updatedTaskRaw);
            dispatch(weddingTaskUpdated(updatedTask));
          } else if (dbWeddingTask) {
            dispatch(weddingTaskUpdated(dbWeddingTask));
          }
        } else if (task.storage === 'user') {
          if (isTaskAlreadyCompletedInState(state, task)) {
            options?.onCompleted?.();
            return;
          }

          const currentUser = selectCurrentUser(state);
          if (!currentUser) throw new Error('Something went wrong');
          const dbUserRef = Users._.getById(currentUser.id);
          const dbUserTaskRef = dbUserRef.Tasks.getById(task.id);
          const dbUserTaskRaw = await dbUserTaskRef.get(true);
          wasTaskAlreadyCompleted = Boolean(dbUserTaskRaw?.done);

          const dbUserTask = dbUserTaskRaw
            ? recursiveSerializeTimestamps(dbUserTaskRaw)
            : undefined;

          if (!wasTaskAlreadyCompleted) {
            const saveTask: Omit<IUserTask, 'createdAt'> = {
              id: task.id,
              group: UsersTasks.todayTaskGroupId,
              custom: false,
              order: 1,
              name: task.name,
              done: true,
            };
            await dbUserTaskRef.set(
              dbUserTaskRaw
                ? { ...saveTask, createdAt: dbUserTaskRaw.createdAt }
                : { ...saveTask, createdAt: serverTimestamp() },
            );
            const updatedTaskRaw = await dbUserTaskRef.get(true);
            const updatedTask = recursiveSerializeTimestamps(updatedTaskRaw);
            dispatch(userTaskUpdated(updatedTask));
          } else if (dbUserTask) {
            dispatch(userTaskUpdated(dbUserTask));
          }
        } else {
          throw new Error('Invalid task passed');
        }

        // Auto-complete mirror task (A/B dual-write)
        const fullTaskDef = registeredTodayTasks[task.id];
        if (fullTaskDef?.mirrorTask && !wasTaskAlreadyCompleted) {
          dispatch(markTodayTaskAsCompleted(fullTaskDef.mirrorTask));
        }

        options?.onCompleted?.();

        if (state.task.todayTasks.status === 'loaded') {
          const taskFlowApiDefinition = getState().task.todayTaskFlowDefinition;
          assertState(taskFlowApiDefinition, 'loaded');
          const taskFlowDefinition = getTaskFlowDefinitionUI(taskFlowApiDefinition.data);
          if (!taskFlowDefinition) return;

          const taskFlowsState = getState().task.taskFlows;
          if (taskFlowsState.status !== 'loaded') return;
          const currentSessionDef = taskFlowDefinition.sessions.find(
            (s) => s.id === taskFlowsState.todayTaskFlow.currentSession.id,
          );
          if (!currentSessionDef) return;
          const currentSessionTaskIds = currentSessionDef.tasks.map((task) => task.id);

          if (!wasTaskAlreadyCompleted && currentSessionTaskIds.includes(task.id)) {
            dispatch(signalTodayTaskFlow());
          }
        }
      } finally {
        if (inFlightKey) markTodayTaskInFlight.delete(inFlightKey);
      }
    };
    asyncAction().catch((error) => {
      SentryMinimal().captureException(error, {
        tags: { source: 'markTodayTaskAsCompleted' },
      });
      options?.onError?.(error);
    });
    return noopAction();
  };

// It updates the current session if it is completed
export const syncTodayTaskFlow =
  () =>
  ({ getState, dispatch }: IDeps) => {
    try {
      const state = getState();
      const market = gazetteer.getMarketByCountry(state.weddings.profile.l10n.country);
      const taskFlowApiDefinition = getState().task.todayTaskFlowDefinition;
      assertState(taskFlowApiDefinition, 'loaded');
      const taskFlowDefinition = getTaskFlowDefinitionUI(taskFlowApiDefinition.data);
      if (!taskFlowDefinition) return noopAction();

      if (state.task.taskFlows.status !== 'loaded') return noopAction();

      const context = {
        getState,
        market,
      };

      const previousTaskFlowInstance = getPreviousTaskFlowInstance(taskFlowDefinition, context);

      if (!previousTaskFlowInstance) return noopAction();

      const nextTaskFlowInstance = getNextTaskFlowInstance(
        taskFlowDefinition,
        context,
        previousTaskFlowInstance,
        false,
      );

      const result = fastForwardCompletedSessions(
        taskFlowDefinition,
        context,
        nextTaskFlowInstance,
      );

      dispatch(taskFlowsUpdated({ todayTaskFlowInstance: result }));

      if (checkIsNewSession(previousTaskFlowInstance, result)) {
        saveTaskFlowInstance(taskFlowDefinition, context, result);
        dispatch(
          newTodaysTaskSessionAnalytics({
            sessionNumber: result.currentSession.id,
          }),
        );
      }
    } catch (e) {
      SentryMinimal().captureException(e, {
        tags: {
          source: 'taskFlows',
        },
      });
    }
    return noopAction();
  };

export const mountTaskComponent = () => () => taskComponentMounted();

export const reinitializeTodayTaskFlowDefinition =
  () =>
  ({ getState, dispatch }: IDeps) => {
    const weddingCountry = getState().weddings.profile.l10n.country;
    const market = gazetteer.getMarketByCountry(weddingCountry);

    const taskFlowApiDefinition = getState().task.todayTaskFlowDefinition;
    assertState(taskFlowApiDefinition, 'loaded');
    const taskFlowDefinition = getTaskFlowDefinitionUI(taskFlowApiDefinition.data);
    if (!taskFlowDefinition) return noopAction();

    const previousTaskFlowInstance = getPreviousTaskFlowInstance(taskFlowDefinition, {
      getState,
      market,
    });

    if (previousTaskFlowInstance) {
      const nextTaskFlowInstance = migrateToNewTaskFlowDefinition(
        taskFlowDefinition,
        previousTaskFlowInstance,
        {
          getState,
          market,
        },
      );

      dispatch(updateTodayTaskFlow({ todayTaskFlowInstance: nextTaskFlowInstance }));
      if (checkIsNewSession(previousTaskFlowInstance, nextTaskFlowInstance)) {
        dispatch(
          newTodaysTaskSessionAnalytics({ sessionNumber: nextTaskFlowInstance.currentSession.id }),
        );
      }
    } else {
      // No stored instance for this flow (e.g. assignment changed to a different flow).
      // Initialize a fresh flow instance starting at session 1.
      const freshInstance = getTaskFlowInstance(taskFlowDefinition, { getState, market });
      dispatch(updateTodayTaskFlow({ todayTaskFlowInstance: freshInstance }));
    }
    return noopAction();
  };

export const {
  taskFlowDefinitionInitialized,
  registerTodayTaskTileData,
  taskFlowsInitialized,
  taskFlowsInitializationFailed,
  updateTodayTaskFlow,
  taskComponentMounted,
  userTasksLoaded,
  weddingTasksLoaded,
  weddingTaskUpdated,
  userTaskUpdated,
  taskFlowsUpdated,
  toggleAddCollaboratorModal,
  updateCarouselFadeIn,
  updateCarouselTask,
} = taskSlice.actions;
